import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Split, Loader2, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      // endpoint always returns 200; still show the confirmation to avoid leaking info
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-foreground text-background p-12 grain-bg">
        <Link to="/" className="flex items-center gap-2" data-testid="forgot-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Split className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl" style={{ fontWeight: 900 }}>BogoShare</span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl leading-tight" style={{ fontWeight: 900 }}>
            Forgot your<br />password?<br /><span className="text-accent">No problem.</span>
          </h2>
          <p className="mt-4 text-background/70 max-w-sm leading-relaxed">
            We'll email you a secure link to set a new one. It expires in an hour and works only once.
          </p>
        </div>
        <p className="text-xs font-mono-code text-background/50">Secure code-exchange settlement</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Split className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl" style={{ fontWeight: 900 }}>BogoShare</span>
          </div>

          {sent ? (
            <div data-testid="forgot-success">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700">
                <MailCheck className="h-6 w-6" />
              </div>
              <h1 className="mt-4 font-heading text-3xl tracking-tight" style={{ fontWeight: 700 }}>Check your inbox</h1>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                If an account exists for <b className="text-foreground">{email}</b>, we've sent a password reset link.
                It expires in 60 minutes. Don't forget to check your spam folder.
              </p>
              <Link to="/login" data-testid="forgot-back-to-login"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                <ArrowLeft className="h-4 w-4" /> Back to log in
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-3xl tracking-tight" style={{ fontWeight: 700 }}>Reset your password</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Enter the email you signed up with and we'll send a reset link.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email" type="email" data-testid="forgot-email-input" value={email} required
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1"
                  />
                </div>
                <Button
                  type="submit" data-testid="forgot-submit-btn" disabled={busy}
                  className="w-full h-11 rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
                >
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send reset link
                </Button>
              </form>
              <Link to="/login" data-testid="forgot-back-link"
                className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                <ArrowLeft className="h-4 w-4" /> Back to log in
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
